import React, { useState } from "react";
import { Typography, Box, Menu, MenuItem } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useLocation } from "react-router-dom";
import useAppStore from "../../stores";

const FrameworkSelector = () => {
  const [frameworkAnchorEl, setFrameworkAnchorEl] = useState(null);
  const frameworkOpen = Boolean(frameworkAnchorEl);
  const location = useLocation();

  // Framework store
  const selectedFrameworkId = useAppStore((state) => state.selectedFrameworkId);
  const selectedFramework = useAppStore((state) => state.selectedFramework);
  const frameworks = useAppStore((state) => state.frameworks);
  const setSelectedFramework = useAppStore(
    (state) => state.setSelectedFramework
  );

  const availableFrameworks = frameworks.filter((f) => f.is_active);
  const selectedFrameworkData = availableFrameworks.find(
    (f) => f.framework_id === selectedFrameworkId
  );

  // Define routes where framework selector should be shown
  const frameworkRequiredRoutes = [
    "/admin/dashboard",
    "/admin/controls",
    "/infrastructure",
    "/admin/integrated-evidences",
  ];

  // Check if current route requires framework selector
  const shouldShowFrameworkSelector = frameworkRequiredRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  const handleFrameworkClick = (event) => {
    setFrameworkAnchorEl(event.currentTarget);
  };

  const handleFrameworkClose = () => {
    setFrameworkAnchorEl(null);
  };

  const handleFrameworkSelect = (frameworkId) => {
    setSelectedFramework(frameworkId);
    handleFrameworkClose();
  };

  // Don't show if no frameworks available or route doesn't require it
  if (
    availableFrameworks.length === 0 ||
    availableFrameworks.length === 1 ||
    !shouldShowFrameworkSelector
  ) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", ml: 3 }}>
      <Typography
        variant="body2"
        sx={{
          color: "#6B7280",
          fontSize: "14px",
          fontWeight: 400,
          mr: 1,
          lineHeight: "20px",
        }}
      >
        Framework:
      </Typography>
      <Box
        onClick={handleFrameworkClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          cursor: "pointer",
          padding: "4px 6px",
          borderRadius: "6px",
          backgroundColor: "transparent",
          "&:hover": {
            backgroundColor: "#F9FAFB",
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#374151",
            lineHeight: "20px",
          }}
        >
          {selectedFrameworkData?.framework_name || "SOC 2"}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{ fontSize: "16px", color: "#6B7280", ml: 0.5 }}
        />
      </Box>

      <Menu
        anchorEl={frameworkAnchorEl}
        open={frameworkOpen}
        onClose={handleFrameworkClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPaper-root": {
            mt: 1,
            minWidth: "180px",
            maxWidth: "220px",
            boxShadow:
              "0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -2px rgba(0, 0, 0, 0.05)",
            borderRadius: "8px",
            border: "1px solid #E5E7EB",
            py: 0.5,
          },
        }}
      >
        {availableFrameworks.map((framework) => (
          <MenuItem
            key={framework.framework_id}
            onClick={() => handleFrameworkSelect(framework.framework_id)}
            selected={framework.framework_id === selectedFrameworkId}
            sx={{
              fontSize: "14px",
              py: 1.5,
              px: 2,
              minHeight: "auto",
              borderRadius: "6px",
              mx: 0.5,
              "&:hover": {
                backgroundColor: "#F3F4F6",
              },
              "&.Mui-selected": {
                backgroundColor: "#EEF2FF",
                "&:hover": {
                  backgroundColor: "#E0E7FF",
                },
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                width: "100%",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight:
                    framework.framework_id === selectedFrameworkId ? 500 : 400,
                  color: "#374151",
                  fontSize: "14px",
                  lineHeight: "20px",
                }}
              >
                {framework.framework_name}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default FrameworkSelector;
